import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, QrCode, Keyboard } from 'lucide-react';
import { parseAppointmentIdFromQr } from '../../utils/parseAppointmentQr';
import { apiClient } from '../../utils/api';

interface QrScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAppointmentFound: (appointmentId: string) => void;
  onError?: (message: string) => void;
  /** API path used to resolve QR / check-in code payloads. Pass null to skip remote resolve. */
  resolvePath?: string | null;
}

async function resolveAppointmentId(
  payload: string,
  resolvePath: string | null | undefined,
): Promise<string | null> {
  const local = parseAppointmentIdFromQr(payload);
  if (local) return local;

  const trimmed = payload.trim();
  if (/^[A-Za-z0-9]{4}$/.test(trimmed) && !resolvePath) {
    return trimmed.toUpperCase();
  }

  if (!resolvePath) return null;

  try {
    const res = await apiClient(
      `${resolvePath}?payload=${encodeURIComponent(payload)}`,
    );
    const data = await res.json();
    if (data.success && data.data?.appointmentId) {
      return data.data.appointmentId as string;
    }
  } catch {
    // fall through
  }
  return null;
}

export function QrScannerModal({
  isOpen,
  onClose,
  onAppointmentFound,
  onError,
  resolvePath = '/api/admin/reception/resolve-qr',
}: QrScannerModalProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const resolvedRef = useRef(false);
  const [manualValue, setManualValue] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [starting, setStarting] = useState(false);
  const [resolving, setResolving] = useState(false);

  const stopScanner = async () => {
    if (!scannerRef.current) return;
    try {
      const state = scannerRef.current.getState();
      if (state === 2) {
        await scannerRef.current.stop();
      }
      scannerRef.current.clear();
    } catch {
      // ignore cleanup errors
    }
    scannerRef.current = null;
  };

  const completeWithAppointment = useCallback(
    async (appointmentId: string) => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      await stopScanner();
      onAppointmentFound(appointmentId);
      onClose();
    },
    [onAppointmentFound, onClose],
  );

  const handleDecoded = useCallback(
    async (text: string, showErrorOnFailure: boolean) => {
      if (resolvedRef.current) return;
      const appointmentId = await resolveAppointmentId(text, resolvePath);
      if (!appointmentId) {
        if (showErrorOnFailure) {
          onError?.('QR code non reconnu. Scannez le code du rendez-vous client.');
        }
        return;
      }
      await completeWithAppointment(appointmentId);
    },
    [completeWithAppointment, onError, resolvePath],
  );

  useEffect(() => {
    if (!isOpen) {
      resolvedRef.current = false;
      setManualValue('');
      setShowManual(false);
      setResolving(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || showManual) return;

    let cancelled = false;
    const start = async () => {
      setStarting(true);
      try {
        await stopScanner();
        const scanner = new Html5Qrcode('qr-reader-region');
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          (decoded) => {
            if (!cancelled && !resolvedRef.current) {
              void handleDecoded(decoded, false);
            }
          },
          () => {},
        );
      } catch {
        if (!cancelled) setShowManual(true);
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    void start();
    return () => {
      cancelled = true;
      void stopScanner();
    };
  }, [isOpen, showManual, handleDecoded]);

  const submitManual = async () => {
    setResolving(true);
    try {
      const appointmentId = await resolveAppointmentId(manualValue, resolvePath);
      if (!appointmentId) {
        onError?.('Identifiant, code d’accueil ou URL de rendez-vous invalide.');
        return;
      }
      await completeWithAppointment(appointmentId);
    } finally {
      setResolving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary-600" />
            <h2 className="font-bold text-lg text-textPrimary">Scanner un QR code</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!showManual ? (
            <>
              <p className="text-sm text-gray-600">
                Placez le QR code du rendez-vous client devant la caméra pour ouvrir sa fiche.
              </p>
              <div
                id="qr-reader-region"
                className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 min-h-[280px]"
              />
              {starting && (
                <p className="text-center text-sm text-gray-500">Démarrage de la caméra…</p>
              )}
              <button
                type="button"
                className="btn-secondary w-full flex items-center justify-center gap-2"
                onClick={() => {
                  void stopScanner();
                  setShowManual(true);
                }}
              >
                <Keyboard className="w-4 h-4" />
                Saisir l&apos;identifiant manuellement
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Collez l&apos;URL, l&apos;identifiant du rendez-vous, ou le code d&apos;accueil à 4 caractères.
              </p>
              <input
                type="text"
                className="input w-full"
                placeholder="Code AB12, URL ou identifiant rendez-vous"
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  type="button"
                  className="btn-secondary flex-1"
                  onClick={() => setShowManual(false)}
                  disabled={resolving}
                >
                  Utiliser la caméra
                </button>
                <button
                  type="button"
                  className="btn-primary flex-1 disabled:opacity-50"
                  onClick={() => void submitManual()}
                  disabled={resolving}
                >
                  {resolving ? 'Vérification…' : 'Ouvrir le rendez-vous'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
